import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { withRouter, useParams } from 'react-router';
import { Card, Button, Row, Col, message, FloatButton, Image, Carousel, Alert } from 'antd';
import { ShoppingCartOutlined } from '@ant-design/icons';
import { useWindowWidth } from '@react-hook/window-size';
import { cartToReserved, companyAuthMode, getCompanyCardPaymentSystem, productType } from '../../extensions/helper';
import { HtmlDescription, getUtmTagId } from '../../extensions/utils';
import Auth from '../auth/auth.js';
import GuestContact from '../auth/guest-contact.js';
import Subscription from './subscription';
import Certificate from './certificate';
import Bookign from './booking';
import { serverFetch } from '../../server';
import { closePaymentWindow, paySale, preparePaymentWindow, shouldOpenPaymentWindow } from '../../extensions/payment-window';
import { cartAdd } from '../../redux/actions';
import PaymentTerms from './payment-terms';
import './catalog.css';
import CatalogHeader from './catalog-header.js';
import { PayButtons } from '../../controls/index.js';

const scrollActivePillIntoView = (groupId) => {
    const bar = document.getElementById('group-header');
    const pill = document.getElementById(`group-${groupId}`);
    if (!bar || !pill) return;
    const barRect = bar.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();
    const delta = pillRect.left - barRect.left - (barRect.width - pillRect.width) / 2;
    bar.scrollBy({ left: delta, behavior: 'smooth' });
};

const emptyGroups = [{ id: 0, name: 'Загрузка ...', description: '' }];
const emptyItems = [
    { id: 0, name: '', description: '', catalogGroupId: 0 }, 
    { id: 1, name: '', description: '', catalogGroupId: 0 }, 
    { id: 2, name: '', description: '', catalogGroupId: 0 },
    { id: 3, name: '', description: '', catalogGroupId: 0 }
];

const Catalog = () => {
    const user = useSelector((s) => s.user);
    const company = useSelector((s) => s.company);
    const cartItems = useSelector((s) => s.cart?.items || []);
    const dispatch = useDispatch();
    const history = useHistory();
    const { id } = useParams();
    const onlyWidth = useWindowWidth();
    const [groups, setGroups] = useState(emptyGroups);
    const [items, setItems] = useState(emptyItems);
    const [currentItem, setCurrentItem] = useState();
    const [currentRents, setCurrentRents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openItem, setOpenItem] = useState(false);
    const [showSignin, setShowSignin] = useState(false);
    const [showGuest, setShowGuest] = useState(false);
    const isFrame = window.self !== window.top;
    const [guestUser, setGuestUser] = useState(null);
    const [scroll, setScroll] = useState(false);
    const [cond1, setCond1] = useState(false);
    const [cond2, setCond2] = useState(false);
    const [cond3, setCond3] = useState(false);
    const [condF, setCondF] = useState(true);
    const [autopay, setAutopay] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [currentGroupId, setCurrentGroupId] = useState();
    const [pendingPaymentSystem, setPendingPaymentSystem] = useState(null);
    const [productImages, setProductImages] = useState([]);
    const currentGroupIdRef = useRef();
    const modalContainerRef = useRef(null);
    const ignoreScrollSpyRef = useRef(false);
    const ignoreScrollSpyTimerRef = useRef(null);
    const paymentWindowRef = useRef(null);

    const selectGroup = useCallback((groupId, { scrollPill = true } = {}) => {
        if (!groupId && groupId !== 0) return;
        const nextId = Number(groupId);
        if (currentGroupIdRef.current === nextId) return;
        currentGroupIdRef.current = nextId;
        setCurrentGroupId(nextId);
        if (scrollPill) scrollActivePillIntoView(nextId);
    }, []);

    useEffect(() => {
        if (company) {
            let cleanup = false;
            const fetchData = async () => {
                setLoading(true);
                const baseParams = { companyId: company.id, isActive: true };
                let promises = [
                    await serverFetch('catalog/groups', { method: 'GET', queryParams: baseParams }, user)
                        .then(data => data)
                        .catch(() => {
                            message.error('Ошибка получения групп каталога');
                            setLoading(false);
                        }),
                    await serverFetch(`catalog/items`, { method: 'GET', queryParams: { ...baseParams, noImage: true } }, user)
                        .then(data => data)
                        .catch(() => {
                            message.error('Ошибка получения элементов каталога');
                            setLoading(false);
                        }),
                ];
                Promise.all([promises]).then((result) => {
                    if (cleanup) return;
                    setGroups(result[0][0] || []);
                    setItems(result[0][1] || []);
                    setLoading(false);
                    setLoaded(true);
                });
            };
            fetchData();
            return () => (cleanup = true);
        }
    }, [company, user]);

    // Восстанавливаем диалог после успешной авторизации
    useEffect(() => { 
        if (user && currentItem && !openItem && !showSignin) {
            setOpenItem(true);
        }
    }, [user, showSignin, currentItem, openItem]);

    // Автоматически продолжаем оплату после авторизации, если была выбрана платежная система
    useEffect(() => {
        if (user && openItem && currentItem && pendingPaymentSystem) {
            const system = pendingPaymentSystem;
            setPendingPaymentSystem(null); // Сразу сбрасываем, чтобы избежать повторного вызова
            
            createSaleAndPay(system);
        }
    }, [user, openItem, pendingPaymentSystem, currentItem, company, currentRents, autopay]);

    // Сброс флага скролла при смене группы из URL (переход с главной)
    useEffect(() => {
        setScroll(false);
    }, [id]);

    // Переход с главной: /catalog/:id → прокрутка к выбранной группе
    useEffect(() => {
        if (!loaded || loading || !id || scroll) return undefined;

        const groupId = String(id);
        let cancelled = false;
        let attempts = 0;
        let retryTimer = null;

        const scrollToGroup = () => {
            if (cancelled) return;

            const element =
                document.getElementById(`catalog-group-${groupId}`) ||
                document.getElementById(groupId);

            if (!element) {
                attempts += 1;
                if (attempts < 20) {
                    retryTimer = setTimeout(scrollToGroup, 100);
                }
                return;
            }

            setScroll(true);
            ignoreScrollSpyRef.current = true;
            selectGroup(groupId);

            const catalogPath = window.location.pathname.replace(/\/catalog\/[^/]+\/?$/, '/catalog');
            window.history.replaceState(null, '', catalogPath);

            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            scrollActivePillIntoView(groupId);

            if (ignoreScrollSpyTimerRef.current) clearTimeout(ignoreScrollSpyTimerRef.current);
            ignoreScrollSpyTimerRef.current = setTimeout(() => {
                ignoreScrollSpyRef.current = false;
            }, 1000);
        };

        // Даём секциям отрисоваться после загрузки данных
        retryTimer = setTimeout(scrollToGroup, 50);

        return () => {
            cancelled = true;
            if (retryTimer) clearTimeout(retryTimer);
        };
    }, [loaded, loading, id, groups, items, scroll, selectGroup]);

    useEffect(() => {
        return () => {
            if (ignoreScrollSpyTimerRef.current) clearTimeout(ignoreScrollSpyTimerRef.current);
        };
    }, []);

    // Синхронизация активной группы с положением скролла
    useEffect(() => {
        if (!loaded || loading) return undefined;

        // Пока ждём прокрутку к группе из URL — не перехватываем активную группу
        if (id && !scroll) {
            ignoreScrollSpyRef.current = true;
            return undefined;
        }

        const sections = Array.from(document.querySelectorAll('.catalog-group'));
        if (!sections.length) return undefined;

        const getSectionGroupId = (section) =>
            section.dataset.groupId || section.id.replace(/^catalog-group-/, '');

        const observer = new IntersectionObserver(
            (entries) => {
                if (ignoreScrollSpyRef.current) return;

                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                if (visible.length > 0) {
                    selectGroup(getSectionGroupId(visible[0].target));
                    return;
                }

                // Если ничего не пересекается (быстрый скролл) — берём ближайшую секцию сверху
                const offset = 170;
                let activeId = getSectionGroupId(sections[0]);
                for (const section of sections) {
                    const top = section.getBoundingClientRect().top;
                    if (top - offset <= 0) activeId = getSectionGroupId(section);
                }
                selectGroup(activeId);
            },
            {
                root: null,
                rootMargin: '-160px 0px -45% 0px',
                threshold: [0, 0.1, 0.25, 0.5],
            }
        );

        sections.forEach((section) => observer.observe(section));

        // начальная синхронизация — не перебиваем группу, выбранную из URL
        if (!ignoreScrollSpyRef.current) {
            const offset = 170;
            let initialId = getSectionGroupId(sections[0]);
            for (const section of sections) {
                if (section.getBoundingClientRect().top - offset <= 0) initialId = getSectionGroupId(section);
            }
            selectGroup(initialId);
        }

        return () => observer.disconnect();
    }, [loaded, loading, groups, items, selectGroup, id, scroll]);

    // Блокировка скролла страницы при открытии модального окна на десктопе
    useEffect(() => {
        if (openItem && onlyWidth >= 744) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [openItem, onlyWidth]);

    // Во фрейме на десктопе модалка прижата к верху — прокручиваем родительскую страницу к ней
    useEffect(() => {
        if (!isFrame || !openItem || onlyWidth < 744 || !modalContainerRef.current) return;
        modalContainerRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, [isFrame, openItem, onlyWidth]);

    // Строка продажи для отправки на сервер из текущего выбора
    const buildSale = () => ({ companyId: company.id, ...currentItem, externalId: null, utmId: getUtmTagId() });
    const buildRents = (sale) =>
        currentRents.map((item) => ({ ...sale, catalogId: 0, productId: item.productId, rentSize: item.size, price: 0, totalPrice: 0, qtyClients: 1, externalId: null }));

    // Создать продажу (или корзину из одной позиции с прокатом) и перейти к оплате
    const createSaleAndPay = (system, actor = user) => {
        const sale = buildSale();
        const request = currentRents.length === 0
            ? serverFetch(`sale`, { method: 'POST', bodyData: sale }, actor)
            : serverFetch(`sale/basket`, { method: 'POST', bodyData: { totalPrice: sale.totalPrice, items: [{ sale, rents: buildRents(sale) }] } }, actor);
        request
            .then((data) => {
                setCurrentItem();
                setPendingPaymentSystem(null);
                paySale(actor, data.id, system, autopay, paymentWindowRef);
            })
            .catch((e) => {
                closePaymentWindow(paymentWindowRef.current);
                paymentWindowRef.current = null;
                setLoading(false);
                e.UserMessage && message.error(e.UserMessage);
            });
    };

    const onPay = (system) => {
        const paymentWindow = preparePaymentWindow(system);
        if (shouldOpenPaymentWindow(system) && !paymentWindow) {
            message.error('Разрешите всплывающие окна для перехода к оплате');
            return;
        }
        paymentWindowRef.current = paymentWindow;

        // Проверяем авторизацию перед оплатой
        if (user) {
            createSaleAndPay(system);
            return;
        }
        if (guestUser) {
            createSaleAndPay(system, guestUser);
            return;
        }
        setPendingPaymentSystem(system); // Сохраняем выбранную платежную систему
        setOpenItem(false);
        if (company?.authMode === companyAuthMode.NoAuth) setShowGuest(true);
        else setShowSignin(true);
    };

    const onGuestSuccess = (session) => {
        setGuestUser(session);
        setShowGuest(false);
        if (pendingPaymentSystem) {
            const system = pendingPaymentSystem;
            setPendingPaymentSystem(null);
            createSaleAndPay(system, session);
        } else if (currentItem) {
            setOpenItem(true);
        }
    };

    const onGuestClose = () => {
        setShowGuest(false);
        if (!user) {
            setPendingPaymentSystem(null);
            closePaymentWindow(paymentWindowRef.current);
            paymentWindowRef.current = null;
        }
    };

    // Добавить текущий выбор в корзину
    const onAddToCart = async () => {
        if (currentItem.productType === productType.Booking) {
            try {
                const times = await serverFetch('room/times', { method: 'POST', bodyData: { companyId: company.id, roomId: currentItem.roomId, qtyClients: currentItem.qtyClients, date: currentItem.date, productId: currentItem.productId, cart: cartToReserved(cartItems, company.id) } }, user);
                if (!times.find((t) => t.id === currentItem.startMin)) {
                    message.error('Недостаточно свободных мест на выбранное время с учётом корзины');
                    return;
                }
            } catch (e) {
                message.error('Ошибка проверки свободных мест');
                return;
            }
        }
        const sale = buildSale();
        const details = [];
        if (currentItem.productType === productType.Booking) {
            if (currentItem.date) details.push(new Date(currentItem.date).toLocaleDateString('ru-RU'));
            if (currentItem.timeName) details.push(currentItem.timeName);
            if (currentItem.roomName) details.push(currentItem.roomName);
            if (currentItem.qtyClients > 1) details.push(`Посетителей: ${currentItem.qtyClients}`);
            if (currentRents.length > 0) details.push(`Прокат: ${currentRents.length}`);
        }
        if (currentItem.productType === productType.Certificate && currentItem.receiverName) details.push(`Получатель: ${currentItem.receiverName}`);
        if (currentItem.promocodeId) details.push('Промокод применён');
        dispatch(cartAdd({
            key: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            companyId: company.id,
            name: currentItem.name,
            productType: currentItem.productType,
            totalPrice: Number(currentItem.totalPrice ?? currentItem.price),
            details,
            sale,
            rents: buildRents(sale),
        }));
        onCancel();
        message.success({
            content: <span>Добавлено в корзину. <Button type='link' size='small' onClick={() => history.push('/cart')}>Перейти в корзину</Button></span>,
        });
    };

    const handleAuthClose = () => {
        setShowSignin(false);
        if (!user) {
            setPendingPaymentSystem(null);
            closePaymentWindow(paymentWindowRef.current);
            paymentWindowRef.current = null;
        }
    };

    const onOpenItem = (item) => {
        setCurrentRents([]);
        setAutopay(item.autopay);
        setCond1(false);
        setCond2(false);
        setCond3(!item.autopay);
        setCondF(true);
        setProductImages([]); // Сбрасываем картинки
        
        // Загружаем картинки для продукта
        if (item.productId) {
            // Для сертификатов используем эндпоинт image/cert, для остальных - product/images
            const endpoint = `product/images/${item.productId}`;
            
            serverFetch(endpoint, { method: 'GET' }, user)
                .then((images) => {
                    setProductImages(images || []);
                    // Если есть картинки, устанавливаем первую по умолчанию
                    if (images && images.length > 0) {
                        setCurrentItem({...item, catalogId: item.id, imageId: images[0].id});
                    } else {
                        setCurrentItem({...item, catalogId: item.id});
                    }
                })
                .catch((e) => {
                    console.log('Ошибка загрузки картинок продукта:', e);
                    setCurrentItem({...item, catalogId: item.id});
                });
        } else {
            setCurrentItem({...item, catalogId: item.id});
        }
        
        const opensPaymentWindow = company && shouldOpenPaymentWindow(getCompanyCardPaymentSystem(company));

        if (!user && company && company.authMode === companyAuthMode.BeforeBuy) {
            setOpenItem(false);
            setShowSignin(true);
        } else if (!user && !guestUser && company && opensPaymentWindow) {
            setOpenItem(false);
            if (company.authMode === companyAuthMode.NoAuth) setShowGuest(true);
            else setShowSignin(true);
        } else {
            setOpenItem(true);
        }
        const message = { sender: 'proffit_scroll' };
        window.parent.postMessage(message, '*');
    };

    const onCancel = () => {
        setCurrentItem();
        setOpenItem(false);
    };

    const onClickHeader = (groupId) => {
        const element =
            document.getElementById(`catalog-group-${groupId}`) ||
            document.getElementById(`${groupId}`);
        if (!element) return;

        setScroll(true);
        selectGroup(groupId);

        // На время программной прокрутки не даём spy перебивать выбранную группу
        ignoreScrollSpyRef.current = true;
        if (ignoreScrollSpyTimerRef.current) clearTimeout(ignoreScrollSpyTimerRef.current);
        ignoreScrollSpyTimerRef.current = setTimeout(() => {
            ignoreScrollSpyRef.current = false;
        }, 800);

        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const groupsWithItems = (groups || []).filter((g) => items.some((i) => i.catalogGroupId === g.id));
    const divMobileHeaders = (
        <CatalogHeader groups={groupsWithItems} currentGroupId={currentGroupId} onClick={onClickHeader} />
    );

    const renderItems = () => {
        const cardSpan = onlyWidth >= 1400 ? 8 : onlyWidth >= 744 ? 12 : 24;
        return (
            <div className='block-content catalog-page'>
                {divMobileHeaders}
                {groups &&
                    groups.map((g) => {
                        const filteredItems = items.filter((i) => i.catalogGroupId === g.id);
                        if (filteredItems.length <= 0) return false;
                        const bannerStyle = g.image
                            ? { backgroundImage: `url("${g.image}")` }
                            : undefined;
                        return (
                            <div
                                key={g.id}
                                id={`catalog-group-${g.id}`}
                                data-group-id={g.id}
                                className='catalog-group'
                            >
                                <div className='catalog-group-banner'>
                                    <div className='catalog-group-banner-layer' style={bannerStyle} aria-hidden='true' />
                                    <div className='catalog-group-banner-text'>
                                        <div className='catalog-group-label'>Категория</div>
                                        <div className='catalog-group-title'>{g.name}</div>
                                    </div>
                                </div>
                                <Row gutter={[16, 16]} className='catalog-group-items'>
                                    {filteredItems.map((i) => {
                                        const tags = (
                                            <>
                                                {i.isBestPrice ? <span className='catalog-badge catalog-badge--best-price'>Лучшая цена</span> : null}
                                                {i.isBestSeller ? <span className='catalog-badge catalog-badge--best-seller'>Хит продаж</span> : null}
                                                {i.isAction ? <span className='catalog-badge catalog-badge--action'>Акция</span> : null}
                                                {company && company.payCredit && !i.autopay && i.price >= 3000 ? (
                                                    <span className='catalog-badge catalog-badge--credit'>
                                                        В кредит от {Math.round(i.price / 20)} ₽/мес
                                                    </span>
                                                ) : null}
                                            </>
                                        );
                                        const divDiscountPrice =
                                            i.productPrice && i.qty === 1 && i.productPrice > i.price ? (
                                                <div className='catalog-price-discount'>{i.productPrice.toLocaleString()} ₽</div>
                                            ) : null;
                                        const divPrice =
                                            i.price && i.price !== 0 ? (
                                                <div className='catalog-item-price'>{i.price.toLocaleString()} ₽</div>
                                            ) : null;
                                        return (
                                            <Col key={i.id} span={cardSpan}>
                                                <Card
                                                    className='catalog-product-card'
                                                    bordered={false}
                                                    loading={loading}
                                                >
                                                    <div className='catalog-product-badges'>{tags}</div>
                                                    <div className='catalog-product-title'>{i.name}</div>
                                                    <div className='catalog-product-description'>
                                                        <HtmlDescription value={i.description} />
                                                    </div>
                                                    <div className='catalog-product-footer'>
                                                        <div className='catalog-product-prices'>
                                                            {divDiscountPrice}
                                                            {divPrice}
                                                        </div>
                                                        {!loading && (
                                                            <Button
                                                                type='primary'
                                                                className='catalog-buy-btn'
                                                                onClick={() => onOpenItem(i)}
                                                            >
                                                                Купить
                                                            </Button>
                                                        )}
                                                    </div>
                                                </Card>
                                            </Col>
                                        );
                                    })}
                                </Row>
                            </div>
                        );
                    })}
            </div>
        );
    };

    const onItemChange = (data) => {
        const newx = Object.assign({...currentItem}, data);
        newx['qty'] = currentItem.qty;
        setCurrentItem(newx);
    }

    const modalContent = (item) => {
        switch (item.productType) {
            case productType.Subscription: return <Subscription item={item} setCurrentItem={onItemChange} />;
            case productType.Certificate: return <Certificate item={item}  setCurrentItem={onItemChange} setCond={setCondF}/>;
            case productType.Booking: return <Bookign item={item} setCurrentRents={setCurrentRents} setCurrentItem={(data) => setCurrentItem((prev) => ({ ...prev, ...data }))} />;
            default: return <></>;
        }
    };

    const divPaymentTerms = (
        <PaymentTerms company={company} autopay={autopay} cond1={cond1} cond2={cond2} cond3={cond3} setCond1={setCond1} setCond2={setCond2} setCond3={setCond3} />
    );

    const incompleteRent = currentRents.some((r) => !r.productId || !r.size);
    const DisableAddToCart = !company || !currentItem || (!currentItem.totalPrice && !currentItem.price) || !condF || incompleteRent
        || (currentItem.productType == productType.Certificate && !currentItem.receiverEmail);
    const DisablePayment = DisableAddToCart || !cond1 || !cond2 || (!cond3 && autopay);
    const divAddToCart = !autopay && currentItem ? (
        <Button className='catalog-add-to-cart' icon={<ShoppingCartOutlined />} disabled={DisableAddToCart} onClick={onAddToCart} block>
            В корзину
        </Button>
    ) : null;

    const modalTotalPrice = currentItem?.totalPrice ?? currentItem?.price;
    const modalCommission = currentItem?.commission;
    const divModalTotalPrice =
        modalTotalPrice != null && modalTotalPrice !== '' && Number(modalTotalPrice) > 0 ? (
            <div className='catalog-modal-summary'>
                <div className='catalog-modal-summary-left'>
                    <div className='catalog-modal-summary-label'>Итого</div>
                    {modalCommission > 0 && (
                        <div className='catalog-modal-summary-commission'>включая комиссию {Number(modalCommission).toLocaleString()} ₽</div>
                    )}
                </div>
                <div className='catalog-modal-summary-value'>
                    {Number(modalTotalPrice).toLocaleString()} ₽
                </div>
            </div>
        ) : null;

    const productTypeLabels = {
        [productType.Subscription]: 'Абонемент',
        [productType.Certificate]: 'Сертификат',
        [productType.Booking]: 'Бронирование',
    };

    const purchaseModal =
        currentItem && openItem
            ? createPortal(
                  <div className={`catalog-modal-backdrop${isFrame ? ' catalog-modal-backdrop--frame' : ''}`} onMouseDown={() => onCancel()}>
                      <div ref={modalContainerRef} className={`catalog-modal-container${isFrame ? ' catalog-modal-container--frame' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
                          <div className='catalog-modal-header'>
                              <div className='catalog-modal-header-text'>
                                  <div className='catalog-modal-title'>{currentItem.name}</div>
                                  <div className='catalog-modal-subtitle'>{productTypeLabels[currentItem.productType]}</div>
                              </div>
                              <button type='button' className='catalog-modal-close' onClick={() => onCancel()} aria-label='Закрыть'>
                                  ×
                              </button>
                          </div>
                          <div className='catalog-modal-body'>
                              <div className='catalog-modal-left'>
                                  {productImages && productImages.length > 0 && (
                                      <div className='catalog-modal-images'>
                                          <Carousel
                                              slidesToShow={1}
                                              slidesToScroll={1}
                                              arrows={onlyWidth >= 744}
                                              dots
                                              afterChange={(e) => onItemChange({ imageId: productImages[e].id })}
                                              adaptiveHeight={false}
                                              infinite={false}
                                          >
                                              {productImages.map((img) => {
                                                  const imageUrl = `${process.env.REACT_APP_API_BASE_URL}/api/image/file/${img.url}`;
                                                  return (
                                                      <div key={img.id} className='catalog-modal-image-slide'>
                                                          <Card
                                                              className='flexible-card catalog-modal-image-card'
                                                              cover={<img alt={img.name} src={imageUrl} className='catalog-modal-image' />}
                                                              styles={{ body: { padding: 0 } }}
                                                              bordered={false}
                                                          />
                                                      </div>
                                                  );
                                              })}
                                          </Carousel>
                                      </div>
                                  )}
                                  <div className='catalog-modal-terms'>
                                      {divPaymentTerms}
                                  </div>
                              </div>
                              <div className='catalog-modal-right'>
                                  <div className='catalog-modal-form'>
                                      {modalContent(currentItem)}
                                      {process.env.REACT_APP_BLOCK_CARD && (
                                          <Alert type='warning' showIcon message='Оплата картами временно ограничена' />
                                      )}
                                  </div>
                              </div>
                          </div>
                          <div className='catalog-modal-pay-footer'>
                              {divModalTotalPrice}
                              <div className='catalog-modal-actions'>
                                  {divAddToCart}
                                  {company && (
                                      <PayButtons
                                          company={company}
                                          isMobile={onlyWidth < 700}
                                          onPay={onPay}
                                          disablePayment={DisablePayment}
                                          autopay={autopay}
                                          price={currentItem.totalPrice}
                                      />
                                  )}
                              </div>
                          </div>
                      </div>
                  </div>,
                  document.body
              )
            : null;

    return (
        <>
            {renderItems()}
            {purchaseModal}
            {showSignin && <Auth onClose={handleAuthClose} />}
            {showGuest && <GuestContact onClose={onGuestClose} onSuccess={onGuestSuccess} />}
            {!openItem && <FloatButton.BackTop />}
        </>
    );
};

export default withRouter(Catalog);
